import {
  CTA,
  DesignTokenSizeEnum,
  FocusLayer,
  LayerWrapper,
  ShimLayer,
  Text,
  TextAlignment,
  TokenTextAppearance,
  TextTag,
  styled,
  IntegratorRoot,
} from '@volkswagen-onehub/components-core';
import { Close } from '@volkswagen-onehub/icons-core';
import { PropsWithChildren, useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { NAVIGATION_HEIGHT } from '@/modules/constants/navigation';
import zIndex from '@/modules/constants/zIndex';
import Box from '../Box/Box';

const StyledCta = styled(CTA)`
  margin: ${(props) => props.theme.size.static300};
  position: absolute;
  right: 0px;
  height: unset;
  width: unset;
`;

const StyledDescription = styled(Text)`
  margin-top: ${(props) => props.theme.size.dynamic0020};
  display: block;
`;

const StyledTextModal = styled.div`
  position: fixed;
  z-index: ${zIndex.MODAL};

  [class^='StyledLayer-'] {
    top: calc(50% + 40px);
    max-height: calc(100% - ${NAVIGATION_HEIGHT + 20}px);
  }
`;

export type TextModalProps = PropsWithChildren<{
  title?: ReactNode;
  description?: ReactNode;
  shortDescription?: ReactNode;
  body?: ReactNode;
  closeModal: (e: React.SyntheticEvent<HTMLElement, Event>) => void;
  openCallback?: () => void;
  closeCallback?: () => void;
}>;

const TextModal = ({
  title,
  description,
  shortDescription,
  body,
  children,
  closeModal,
  openCallback,
  closeCallback,
}: TextModalProps) => {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    if (openCallback) openCallback();

    return () => {
      document.body.style.overflow = 'unset';
      if (closeCallback) closeCallback();
    };
  }, []);

  const portalContainer = document.getElementById('portal-container');

  return createPortal(
    <StyledTextModal>
      <LayerWrapper>
        <ShimLayer key="shim" onClick={(e) => closeModal(e)} />
        <FocusLayer key={'focus'} active size="A">
          <StyledCta
            tag="button"
            emphasis="tertiary"
            onClick={(e: any) => closeModal(e)}
            aria-label="close"
            icon={<Close />}
          />
          <Box
            padding={DesignTokenSizeEnum.static450}
            pr={DesignTokenSizeEnum.static530}
          >
            {title && (
              <Box
                as={Text}
                mb={DesignTokenSizeEnum.static250}
                bold
                appearance={TokenTextAppearance.headline200}
                textAlign={TextAlignment.left}
                tag={TextTag.h3}
              >
                {title}
              </Box>
            )}
            {shortDescription && (
              <StyledDescription
                appearance={TokenTextAppearance.copy200}
                textAlign={TextAlignment.left}
              >
                {shortDescription}
              </StyledDescription>
            )}
            {description && (
              <StyledDescription
                appearance={TokenTextAppearance.copy200}
                textAlign={TextAlignment.left}
              >
                {description}
              </StyledDescription>
            )}
            {(body || children) && (
              <Box mt={DesignTokenSizeEnum.static300}>{children || body}</Box>
            )}
          </Box>
        </FocusLayer>
      </LayerWrapper>
    </StyledTextModal>,
    portalContainer ? portalContainer : document.body
  );
};

export default TextModal;
